import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiFehler, type Aufgabe, type AufgabeBezugTyp, type AufgabeKonflikt } from '../api';
import { sync, darf } from '../sync';
import { datumKurz, eintragen, faelligkeit, heuteIso, nrText, zumBezug } from '../aufgaben';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { offeneAufgabenLaden } from '../offeneAufgaben';
import { toast } from './toast';
import { useNachladen } from './nachladen';
import { menueOeffnen, adresse, kopieren } from './Kontextmenue';
import { aufgabeMenue } from '../kontextmenue';

const NACHLADEN_MS = 15_000;

/**
 * Block „Aufgaben“ im Detailbereich (Schritt, Prozess, Entscheidung): Aufgaben mit diesem Bezug, Abhaken und
 * „Aufgabe anlegen“ (Titel, Enter – mit Bezug). Lesen alle Mitglieder; anlegen und abhaken ab Mitwirkend.
 * Ein Klick auf eine Aufgabe öffnet sie in der Aufgabenansicht (/p/<projekt>/aufgaben/<id>).
 */
export function AufgabenBlock({ bezugTyp, bezugId }: { bezugTyp: AufgabeBezugTyp; bezugId: string }) {
  const projektId = sync.projektId;
  const navigate = useNavigate();
  const [liste, setListe] = useState<Aufgabe[] | null>(null);
  const [behalten, setBehalten] = useState<Set<string>>(new Set());
  const [erledigteZeigen, setErledigteZeigen] = useState(false);
  const [titel, setTitel] = useState('');
  const [sendet, setSendet] = useState(false);
  const aenderungen = useRef(0); // verwirft Nachlade-Ergebnisse, die eigene Änderungen überholen würden
  const darfSchreiben = darf('mitwirkend');
  const heute = heuteIso();

  const laden = async () => {
    const vorher = aenderungen.current;
    try {
      const l = await api.aufgaben(projektId, { bezugTyp, bezugId });
      if (aenderungen.current === vorher) setListe(l);
    } catch { /* Nachladen versucht es erneut */ }
  };
  useEffect(() => {
    setListe(null);
    setBehalten(new Set());
    aenderungen.current++; // Antworten zum vorigen Bezug verwerfen
    void laden();
  }, [projektId, bezugTyp, bezugId]);
  useNachladen(() => void laden(), NACHLADEN_MS, [projektId, bezugTyp, bezugId]);

  const anlegen = async () => {
    const t = titel.trim();
    if (!t || sendet) return;
    setSendet(true);
    aenderungen.current++;
    try {
      const a = await api.aufgabeAnlegen(projektId, { titel: t, bezugTyp, bezugId });
      aenderungen.current++;
      setListe((l) => eintragen(l ?? [], a));
      setTitel('');
      void offeneAufgabenLaden(projektId);
    } catch (e) {
      toast('Aufgabe nicht angelegt: ' + (e as Error).message, true);
    } finally {
      setSendet(false);
    }
  };
  const abhaken = async (a: Aufgabe) => {
    setBehalten((b) => new Set(b).add(a.id));
    aenderungen.current++;
    setListe((l) => l && eintragen(l, { ...a, erledigt: !a.erledigt }));
    try {
      const r = await api.aufgabeAendern(projektId, a.id, a.zeile, { erledigt: !a.erledigt });
      aenderungen.current++;
      setListe((l) => l && eintragen(l, r.aufgabe));
      void offeneAufgabenLaden(projektId);
    } catch (e) {
      aenderungen.current++;
      const k = e instanceof ApiFehler && e.status === 409 ? (e.daten as AufgabeKonflikt | undefined) : undefined;
      if (k?.aktuell) {
        setListe((l) => l && eintragen(l, k.aktuell));
        toast('Die Aufgabe wurde inzwischen geändert – angezeigt wird der aktuelle Stand.', true);
      } else {
        toast('Aufgabe nicht gespeichert: ' + (e as Error).message, true);
        void laden();
      }
    }
  };
  const oeffnen = (a: Aufgabe) => navigate(`/p/${encodeURIComponent(projektId)}/aufgaben/${encodeURIComponent(a.id)}`);
  const taste = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void anlegen(); }
    if (e.key === 'Escape') { e.stopPropagation(); setTitel(''); (e.target as HTMLInputElement).blur(); }
  };

  const { offen, erledigt } = zumBezug(liste ?? [], behalten);
  const anzahlOffen = (liste ?? []).filter((a) => !a.erledigt).length;

  const zeile = (a: Aufgabe) => (
    <div key={a.id} className={'aufgabe-zeile klein' + (a.erledigt ? ' erledigt' : '')} role="button" tabIndex={0} title="Aufgabe öffnen"
      onClick={() => oeffnen(a)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) oeffnen(a); }} data-testid={'bezug-aufgabe-' + a.id}
      onContextMenu={(e) => menueOeffnen(e, aufgabeMenue(a, { darfSchreiben, darfLoeschen: false }, {
        oeffnen: () => oeffnen(a),
        abhaken: () => abhaken(a),
        linkKopieren: () => kopieren(adresse(`/p/${encodeURIComponent(projektId)}/aufgaben/${encodeURIComponent(a.id)}`)),
      }, heute))}>
      <input type="checkbox" className="haken" checked={a.erledigt} disabled={!darfSchreiben} title={a.erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'}
        onClick={(e) => e.stopPropagation()} onChange={() => void abhaken(a)} data-testid="aufgabe-haken" />
      <span className="lfd-nr" title="ID der Aufgabe">{nrText(a.nr)}</span>
      <span className="titel">{a.titel}</span>
      {a.faellig && <span className={'faellig ' + (a.erledigt ? '' : faelligkeit(a.faellig, heute))} title={'Fällig am ' + a.faellig}>{datumKurz(a.faellig, heute)}</span>}
      {a.zustaendig && <Avatar email={a.zustaendig} size={20} />}
    </div>
  );

  return (
    <section className="bezug-aufgaben" data-testid={'aufgaben-' + bezugTyp}>
      <h3>Aufgaben{anzahlOffen > 0 && <span className="badge-count" style={{ marginLeft: 6 }}>{anzahlOffen}</span>}</h3>
      {liste === null && <p className="hint">Aufgaben werden geladen …</p>}
      {liste && liste.length === 0 && <p className="hint">Noch keine Aufgaben.</p>}
      {offen.map(zeile)}
      {erledigt.length > 0 && (
        <button className="linkish" onClick={() => setErledigteZeigen(!erledigteZeigen)} data-testid="bezug-aufgaben-erledigte">
          {erledigteZeigen ? 'Erledigte ausblenden' : `${erledigt.length} erledigte anzeigen`}
        </button>
      )}
      {erledigteZeigen && erledigt.map(zeile)}
      {darfSchreiben && (
        <div className="schnell-anlegen">
          <Icon name="plus" size={15} />
          <input value={titel} placeholder="Aufgabe anlegen … (Enter)" onChange={(e) => setTitel(e.target.value)} onKeyDown={taste}
            disabled={sendet} data-testid="bezug-aufgabe-neu" />
        </div>
      )}
    </section>
  );
}
